import { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff } from "lucide-react";

const FIELDS = [
  { key: "atl_portal_email", label: "ATL Portal Email ID", isPassword: false, full: true },
  { key: "pfms_id", label: "PFMS ID" },
  { key: "pfms_password", label: "PFMS Password", isPassword: true },
  { key: "pfms_da_id", label: "PFMS DA ID" },
  { key: "pfms_da_password", label: "PFMS DA Password", isPassword: true },
  { key: "pfms_do_id", label: "PFMS DO ID" },
  { key: "pfms_do_password", label: "PFMS DO Password", isPassword: true },
  { key: "atl_dashboard_id", label: "ATL Dashboard ID" },
  { key: "atl_dashboard_password", label: "ATL Dashboard Password", isPassword: true },
];

// Bharatkosh — applicable to both ATL 1.0 and ATL 2.0
const BHARATKOSH_FIELDS = [
  { key: "bharatkosh_email", label: "Bharatkosh ID (Email ID)", isPassword: false, full: true },
  { key: "bharatkosh_password", label: "Bharatkosh Password", isPassword: true },
  { key: "bharatkosh_mobile", label: "Registered Mobile Number" },
];

// ATL 2.0 specific fields — shown only when the client is ATL 2.0
const ATL2_FIELDS = [
  { key: "atl2_gmail_id", label: "Gmail ID (used for ATL 2.0 Portal)", isPassword: false, full: true },
  { key: "atl2_gmail_password", label: "Gmail Password", isPassword: true },
  { key: "atl2_portal_user_id", label: "ATL 2.0 Portal User ID" },
  { key: "atl2_portal_password", label: "ATL 2.0 Portal Password", isPassword: true },
];

const _isATL2 = (client) => {
  if (!client) return false;
  if ((client.client_type || "").trim() === "ATL 2.0") return true;
  return (client.client_types || []).some((t) => String(t).trim() === "ATL 2.0");
};

export default function SchoolCredentialsDialog({ open, onOpenChange, client }) {
  const [creds, setCreds] = useState({});
  const [show, setShow] = useState({});
  const [saving, setSaving] = useState(false);
  const [appCode, setAppCode] = useState("");
  const isATL2 = _isATL2(client);

  useEffect(() => {
    if (open && client?.id) {
      api.get(`/crm/clients/${client.id}/school-credentials`)
        .then(({ data }) => setCreds(data || {}))
        .catch((e) => toast.error(formatError(e.response?.data?.detail)));
      setShow({});
      setAppCode("");
      // Auto-fetch ATL Application Code from doc-status (only useful for ATL 2.0)
      if (_isATL2(client)) {
        api.get(`/crm/clients/${client.id}/doc-status`)
          .then(({ data }) => {
            const code = data?.documentation_status?.atl2_application_code || "";
            setAppCode(code);
          })
          .catch(() => { /* silent — not all users have access */ });
      }
    }
  }, [open, client?.id]); // eslint-disable-line

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/crm/clients/${client.id}/school-credentials`, creds);
      toast.success("Credentials saved");
      onOpenChange(false);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="school-creds-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <KeyRound size={16} className="text-amber-700" /> School Credentials — {client?.client_name}
          </DialogTitle>
          <div className="text-[11px] mono text-slate-500">
            U-DISE · {creds.u_dise || client?.u_dise || "—"}{creds.atl_code ? ` · ATL Code: ${creds.atl_code}` : ""}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">🔒 Sensitive. Visible to the assigned doc team and CRM Manager / Master Admin only.</div>
        </DialogHeader>

        <div className="py-2 grid grid-cols-1 md:grid-cols-2 gap-3">
          {isATL2 && (
            <div className="md:col-span-2 border-2 border-violet-200 bg-violet-50/40 rounded-md p-3" data-testid="creds-atl2-info">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 mb-1">ATL 2.0 Application</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-slate-700">Application Code (auto-fetched from Stage 2 status):</span>
                <span className="mono font-bold text-violet-800 text-sm" data-testid="creds-atl2-appcode">
                  {appCode || <span className="text-slate-400 italic font-normal">— not filled yet</span>}
                </span>
              </div>
            </div>
          )}
          {[...FIELDS].map((f) => (
            <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{f.label}</Label>
              <div className="relative">
                <Input
                  value={creds[f.key] || ""}
                  type={f.isPassword && !show[f.key] ? "password" : "text"}
                  onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                  className="mt-1.5 pr-10 mono"
                  data-testid={`creds-${f.key}`}
                />
                {f.isPassword && (
                  <button
                    type="button"
                    onClick={() => setShow({ ...show, [f.key]: !show[f.key] })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-slate-500 hover:text-slate-800 p-1"
                    data-testid={`creds-show-${f.key}`}
                    title={show[f.key] ? "Hide" : "Show"}
                  >
                    {show[f.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Bharatkosh — applies to both ATL 1.0 and ATL 2.0 */}
          <div className="md:col-span-2 mt-2 border-t pt-3" data-testid="creds-bharatkosh-section">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700 mb-1">Bharatkosh</div>
            <div className="text-[11px] text-slate-500">Common for ATL 1.0 &amp; ATL 2.0</div>
          </div>
          {BHARATKOSH_FIELDS.map((f) => (
            <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{f.label}</Label>
              <div className="relative">
                <Input
                  value={creds[f.key] || ""}
                  type={f.isPassword && !show[f.key] ? "password" : "text"}
                  onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                  className="mt-1.5 pr-10 mono"
                  data-testid={`creds-${f.key}`}
                />
                {f.isPassword && (
                  <button
                    type="button"
                    onClick={() => setShow({ ...show, [f.key]: !show[f.key] })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-slate-500 hover:text-slate-800 p-1"
                    data-testid={`creds-show-${f.key}`}
                    title={show[f.key] ? "Hide" : "Show"}
                  >
                    {show[f.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>
            </div>
          ))}

          {isATL2 && (
            <div className="md:col-span-2 mt-2 border-t pt-3" data-testid="creds-atl2-section">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-violet-700 mb-1">ATL 2.0 Portal Access</div>
            </div>
          )}
          {(isATL2 ? ATL2_FIELDS : []).map((f) => (
            <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{f.label}</Label>
              <div className="relative">
                <Input
                  value={creds[f.key] || ""}
                  type={f.isPassword && !show[f.key] ? "password" : "text"}
                  onChange={(e) => setCreds({ ...creds, [f.key]: e.target.value })}
                  className="mt-1.5 pr-10 mono"
                  data-testid={`creds-${f.key}`}
                />
                {f.isPassword && (
                  <button
                    type="button"
                    onClick={() => setShow({ ...show, [f.key]: !show[f.key] })}
                    className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-slate-500 hover:text-slate-800 p-1"
                    data-testid={`creds-show-${f.key}`}
                    title={show[f.key] ? "Hide" : "Show"}
                  >
                    {show[f.key] ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {creds.updated_by_name && (
          <div className="text-[10px] text-slate-500 mono mt-1">Last updated by {creds.updated_by_name} · {creds.updated_at}</div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="creds-save">
            {saving ? "Saving…" : "Save Credentials"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
